import type z from 'zod/v4'
import { relations } from 'drizzle-orm'
import { boolean, index, integer, pgTable, text, timestamp, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'

import { BookFormat, BookLevel, BookTheme } from '../book'
import { Contributor } from '../contributor'
import { Challenge } from '../lesson'
import { AchievementConditionGroup } from './achievement-condition-group'

export const AchievementCondition = pgTable(
  'achievement_condition',
  {
    id: uuid().notNull().primaryKey().defaultRandom(),
    conditionGroupId: uuid()
      .notNull()
      .references(() => AchievementConditionGroup.id, { onDelete: 'cascade' }),
    conditionType: text({
      enum: [
        'TOTAL_BOOKS_COMPLETED',
        'TOTAL_READING_DURATION',
        'TOTAL_LESSONS_COMPLETED',
        'TOTAL_XP_EARNED',
        'CHALLENGE_COMPLETED',
        'READING_STAMINA',
      ],
    }).notNull(),
    bookFormatId: uuid().references(() => BookFormat.id, { onDelete: 'set null' }),
    bookLevelId: uuid().references(() => BookLevel.id, { onDelete: 'set null' }),
    bookThemeId: uuid().references(() => BookTheme.id, { onDelete: 'set null' }),
    bookContributorId: uuid().references(() => Contributor.id, { onDelete: 'set null' }),
    targetValue: integer().notNull(),
    challengeId: uuid().references(() => Challenge.id, { onDelete: 'set null' }),
    isRequired: boolean().default(true),
    order: integer().notNull().default(0),
    createdAt: timestamp({ mode: 'date', withTimezone: true, precision: 3 }).notNull(),
    updatedAt: timestamp({ mode: 'date', withTimezone: true, precision: 3 }),
  },
  (table) => [
    index('achievement_condition_condition_group_id_idx').on(table.conditionGroupId),
    index('achievement_condition_condition_type_idx').on(table.conditionType),
    index('achievement_condition_challenge_id_idx').on(table.challengeId),
    index('achievement_condition_order_idx').on(table.order),
    index('achievement_condition_created_at_idx').on(table.createdAt),
    index('achievement_condition_updated_at_idx').on(table.updatedAt),
  ],
)

export const achievementConditionRelations = relations(AchievementCondition, ({ one }) => ({
  conditionGroup: one(AchievementConditionGroup, {
    fields: [AchievementCondition.conditionGroupId],
    references: [AchievementConditionGroup.id],
  }),
  challenge: one(Challenge, {
    fields: [AchievementCondition.challengeId],
    references: [Challenge.id],
  }),
}))

export const InsertAchievementConditionSchema = createInsertSchema(AchievementCondition).omit({ id: true })
export const UpdateAchievementConditionSchema = createUpdateSchema(AchievementCondition).omit({ id: true }).partial()
export const SelectAchievementConditionSchema = createSelectSchema(AchievementCondition)

export type InsertAchievementCondition = typeof AchievementCondition.$inferInsert
export type UpdateAchievementCondition = z.infer<typeof UpdateAchievementConditionSchema>
export type SelectAchievementCondition = typeof AchievementCondition.$inferSelect
